import React, { useCallback, useEffect, useState } from 'react';
import { Drawer, Toolbar, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import styled, { css, keyframes } from 'styled-components';
import AdminNav, { useHasAdminNav } from './AdminNav';
import NavTabs, { NavTab } from './NavTabs';
import RestaurantNav from './RestaurantNav';
import UserNav from './UserNav';
import { isOverviewPage, isRestaurantSubPage, useSelectedShop } from './useSelectedShop';

export const DRAWER_WIDTH = 320;

const NAV_GUTTER = 48;

type SideDrawerProps = {
  isOpen: boolean;
  handleDrawerToggle: (open?: boolean) => void;
};

const SideDrawer: React.FC<SideDrawerProps> = ({ isOpen, handleDrawerToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile) handleDrawerToggle(false);
  }, [isMobile, handleDrawerToggle]);

  // On small screens the drawer covers the page.
  const closeOnMobile = useCallback(() => {
    if (isMobile) handleDrawerToggle(false);
  }, [isMobile, handleDrawerToggle]);

  const hasAdminNav = useHasAdminNav();

  // Here rather than in RestaurantNav, so the remembered shop is validated whichever tab is showing.
  const { selectedShopId, allowedShops, isLoading: isPermissionsLoading } = useSelectedShop();

  // The tab follows the page; a tab picked by hand holds only until the next navigation. The overview of a
  // user's restaurants belongs to the restaurant tab, where its nav item lives — but only when they have
  // several, since that is when the item exists; for everyone else `/` is an admin landing page.
  const { pathname } = useLocation();
  const isRestaurantPage = isRestaurantSubPage(pathname) || (isOverviewPage(pathname) && allowedShops.length > 1);
  const routeTab: NavTab = isRestaurantPage ? 'restaurant' : 'admin';
  const [pickedTab, setPickedTab] = useState<{ tab: NavTab; pathname: string }>();
  const tab = pickedTab?.pathname === pathname ? pickedTab.tab : routeTab;
  const handleTabChange = (next: NavTab) => setPickedTab({ tab: next, pathname });

  // Users with no shops of their own (admins) only get the admin nav.
  const hasRestaurantNav = allowedShops.length > 0;
  const hasTabs = hasAdminNav && hasRestaurantNav;
  const onlyTab: NavTab = hasRestaurantNav ? 'restaurant' : 'admin';
  const activeTab = hasTabs ? tab : onlyTab;

  // The replaced nav is kept as `leaving` while it slides out. `from` stays unset until the first switch, so
  // nothing animates on load — including a switch the permissions force as they arrive (the roles and the
  // shops come from different queries, so `/` can read as the admin tab for a frame): that one snaps.
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const [slide, setSlide] = useState<{ tab: NavTab; from?: SlideFrom; leaving?: NavTab }>({ tab: activeTab });
  if (slide.tab !== activeTab) {
    setSlide(
      isPermissionsLoading
        ? { tab: activeTab }
        : {
            tab: activeTab,
            from: activeTab === 'restaurant' ? 'right' : 'left',
            // With reduced motion no animationend would ever remove it.
            leaving: prefersReducedMotion ? undefined : slide.tab,
          }
    );
  }
  const panels = slide.leaving ? [slide.leaving, activeTab] : [activeTab];

  return (
    <nav>
      <StyledDrawer
        variant="persistent"
        anchor="left"
        open={isOpen}
        onClose={() => handleDrawerToggle()}
        ModalProps={{ keepMounted: true }}
      >
        <Toolbar />
        <DrawerContent>
          {hasTabs && <NavTabs value={activeTab} onChange={handleTabChange} />}
          <NavViewport>
            {panels.map((panelTab) => {
              const leaving = panelTab !== activeTab;
              return (
                <NavPanel
                  key={panelTab}
                  $from={slide.from}
                  $leaving={leaving}
                  aria-hidden={leaving || undefined}
                  onAnimationEnd={(event) => {
                    // `animationend` bubbles up from children too.
                    if (leaving && event.target === event.currentTarget) {
                      setSlide((current) => ({ ...current, leaving: undefined }));
                    }
                  }}
                >
                  {panelTab === 'admin' ? (
                    <AdminNav onNavigate={closeOnMobile} />
                  ) : (
                    <RestaurantNav
                      selectedShopId={selectedShopId}
                      allowedShops={allowedShops}
                      onNavigate={closeOnMobile}
                    />
                  )}
                </NavPanel>
              );
            })}
          </NavViewport>
          <UserNavSlot>
            <UserNav />
          </UserNavSlot>
        </DrawerContent>
      </StyledDrawer>
    </nav>
  );
};

// Full screen below `md`.
const StyledDrawer = styled(Drawer)`
  width: 100vw;
  flex-shrink: 0;

  & .MuiDrawer-paper {
    width: 100vw;
    box-sizing: border-box;
    background-color: ${({ theme }) => theme.palette.navigation.main};
    border-right: none;
    height: 100%;
    /* Stable gutter, so switching between the long and short nav doesn't shift the content sideways. */
    scrollbar-gutter: stable;
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => alpha(theme.palette.common.white, 0.4)} transparent;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    &,
    & .MuiDrawer-paper {
      width: ${DRAWER_WIDTH}px;
    }
  }
`;

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 0 ${NAV_GUTTER}px ${({ theme }) => theme.spacing(5)};
`;

type SlideFrom = 'left' | 'right';

// `--slide-dir` is 1 when the new nav enters from the right, -1 from the left; the old one leaves the other way.
const slideIn = keyframes`
  from { transform: translateX(calc(var(--slide-dir) * 100%)); }
`;

const slideOut = keyframes`
  to { transform: translateX(calc(var(--slide-dir) * -100%)); }
`;

// Spans the paper's full width, so the navs slide at the drawer's edge. Clipping keeps a taller leaving nav
// from stretching the scroll height.
const NavViewport = styled.div`
  position: relative;
  overflow: clip;
  margin: 0 -${NAV_GUTTER}px -8px;
  /* Room for the last item's hover pill, which reaches 8px below it. */
  padding-bottom: 8px;
`;

const NavPanel = styled.div<{ $from?: SlideFrom; $leaving: boolean }>(
  ({ theme, $from, $leaving }) => css`
    padding: 0 ${NAV_GUTTER}px;
    /* Keeps the first item's top margin inside the panel, so the leaving and entering navs line up. */
    display: flow-root;

    ${$from &&
    css`
      --slide-dir: ${$from === 'right' ? 1 : -1};
      animation: ${$leaving ? slideOut : slideIn} ${theme.transitions.duration.standard}ms
        ${theme.transitions.easing.easeInOut} both;
    `}

    ${$leaving &&
    css`
      position: absolute;
      inset: 0 0 auto;
      pointer-events: none;
    `}

    @media (prefers-reduced-motion: reduce) {
      animation: none;
    }
  `
);

const UserNavSlot = styled.div`
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing(6)};
`;

export default SideDrawer;
